import {
  FormButtonType,
  IDocument,
  IFormButton,
} from 'app/services/api5-service/api.interface';
import { BehaviorSubject } from 'rxjs';

import { ThemePalette } from '@ispui/button';

import { DocHelper } from 'utils/dochelper';

import { IFormButtonUi } from './model/form-button.interface';

/** list of button types that can be used to submit the form */
const SUBMIT_BUTTON_TYPE_LIST: FormButtonType[] = [
  FormButtonType.Ok,
  FormButtonType.Next,
  FormButtonType.Submit,
  FormButtonType.Blank,
];

/**
 * Is it a button to submit the form or not
 *
 * @param button - button meta
 */
function isSubmitButton(button: IFormButton): boolean {
  return SUBMIT_BUTTON_TYPE_LIST.includes(button.$type);
}

/**
 * Is it a button with delete action
 *
 * @param button - button meta
 */
function isDeleteButton(button: IFormButton): boolean {
  // @HACK this button have special displaying in ispmgr
  return button.$name === 'delete';
}

/**
 * Is it a button for busket cleaning
 *
 * @param button - button meta
 */
function isCleanBusketButton(button: IFormButton): boolean {
  // @HACK this button have special displaying in billmgr
  return button.$name === 'clearbasket';
}

/**
 * Is it a button with settings action
 *
 * @param button - button meta
 */
function isSettingButton(button: IFormButton): boolean {
  // @HACK this button have special displaying in ispmgr
  return button.$name === 'settings';
}

/**
 * Is it a button cancel action
 *
 * @param button - button meta
 */
function isCancelButton(button: IFormButton): boolean {
  return button.$type === FormButtonType.Cancel || button.$name === 'cancel';
}

/**
 * Get msg from doc
 *
 * @param doc - doc instance
 * @param code - msg code
 */
function getMsg(doc: IDocument, code: string): string {
  return DocHelper.getMessage(code, doc);
}

/**
 * Get button title
 *
 * @param doc - doc instance
 * @param button - button meta
 */
function getButtonLabel(doc: IDocument, button: IFormButton): string {
  return isDeleteButton(button) || isSettingButton(button)
    ? ''
    : getMsg(doc, `msg_${button.$name}`);
}

/**
 * Get hint for button
 *
 * @param doc - doc instance
 * @param name - button name
 * @param forDisabledState - is hint for button disabled state
 */
function getHint(
  doc: IDocument,
  name: string,
  forDisabledState: boolean,
): string {
  const suffix = forDisabledState ? '_disabled' : '';
  return getMsg(doc, `hint_${name}${suffix}`);
}

/**
 * Get button display type
 *
 * @param button - button metadata
 */
function getButtonDisplayType(
  button: IFormButton,
): IFormButtonUi['displayType'] {
  // @HACK only 'cancel' button displayed as a link
  return isCancelButton(button) || button.$theme === 'link' ? 'link' : 'button';
}

/**
 * Get button theme
 *
 * @param button - button metadata
 * @param buttons - all buttons
 */
function getButtonTheme(
  button: IFormButton,
  buttons: IFormButton[],
): ThemePalette {
  if (['yellow'].includes(button.$color)) {
    return 'accent';
  }

  if (isCleanBusketButton(button)) {
    return 'fourth';
  }

  const isInstallButton = button.$name === 'install';
  const isThereSomeBuyButton = buttons.some(btn => btn.$name === 'buy');
  // @HACK for ispmgr modules install page
  const specialIspMgrCase = isInstallButton && isThereSomeBuyButton;

  if (
    isDeleteButton(button) ||
    isSettingButton(button) ||
    specialIspMgrCase ||
    !isSubmitButton(button) ||
    button.$theme === 'sixth'
  ) {
    return 'sixth';
  }

  return 'primary';
}

/**
 * Get icon for button or link
 *
 * @param button - button metadata
 */
function getIcon(button: IFormButton): string | undefined {
  // @HACK use certain button icons depending on button names
  if (isDeleteButton(button) || isCleanBusketButton(button)) {
    return 't-busket';
  } else if (isSettingButton(button)) {
    return 'p-attr';
  }
  return undefined;
}

/**
 * Get button color
 *
 * @param button - button mapped metadata
 */
function getButtonColor(button: IFormButtonUi): 'accent' | undefined {
  // @HACK we need to use 'accent' color for this icon, cause it has 'red' color
  if (isDeleteButton(button) || isCleanBusketButton(button)) {
    return 'accent';
  }
  return undefined;
}

/**
 * Get button list for dynamic-form footer or for buttons in list field row
 *
 * @param doc - doc instance
 * @param buttons - buttons metadata
 * @param showHints - is hints can be shown
 * @param isDisableSubmitButton - disable submit button
 */
export function getButtonList(
  doc: IDocument,
  buttons: IFormButton[],
  showHints: boolean,
  isDisableSubmitButton?: boolean,
): IFormButtonUi[] {
  return buttons.map(button => {
    const btn: IFormButtonUi = {
      ...button,
      id: Symbol('buttonId'),
      label: getButtonLabel(doc, button),
      displayType: getButtonDisplayType(button),
      icon: getIcon(button),
      preloaderSubject: new BehaviorSubject(false),
      disabledSubject: new BehaviorSubject(button.$disabled === 'yes'),
    };

    if (isDisableSubmitButton && isSubmitButton(button)) {
      btn.disabledSubject.next(true);
    }

    if (btn.displayType === 'button') {
      btn.buttonTheme = getButtonTheme(button, buttons);
      btn.buttonColor = getButtonColor(btn);
    }

    if (showHints) {
      btn.hint = getHint(doc, button.$name, false);
      btn.hintDisabled = getHint(doc, button.$name, true);
    }

    if (doc.metadata?.form?.$target) {
      btn.$act = doc.metadata.form.$target;
    }

    return btn;
  });
}

/**
 * Get the form submit button from the general list of buttons
 *
 * @param buttonList - list of buttons to search for form submit button
 */
export function getFirstSubmitButton(
  buttonList: IFormButtonUi[],
): IFormButtonUi | undefined {
  return buttonList.find(button => isSubmitButton(button));
}

/**
 * Get the list of form submit button from the general list of buttons
 *
 * @param buttonList - list of buttons to search for form submit button
 */
export function getSubmitButtonsList(
  buttonList: IFormButtonUi[],
): IFormButtonUi[] {
  return buttonList.filter(button => isSubmitButton(button));
}

/**
 * Set the new disable state of the form submit button
 *
 * @param buttonList - list of buttons to search for form submit button
 * @param state - the button will be disabled or not
 */
export function setSubmitButtonDisableState(
  buttonList: IFormButtonUi[],
  state: boolean,
): void {
  const submitButtonsList = getSubmitButtonsList(buttonList);

  for (const submitButton of submitButtonsList) {
    const newState = state;
    submitButton.disabledSubject.next(newState);
  }
}

/**
 * Get drawer form buttons
 *
 * @param doc - doc
 * @param drawerSelectName - drawer name
 */
export function getDrawerFormButtons(
  doc: IDocument,
  drawerSelectName: string,
): IFormButtonUi[] {
  return [
    {
      id: Symbol('buttonId'),
      $name: 'DrawerButtonOk',
      $type: FormButtonType.Ok,
      label: DocHelper.getMessage(`${drawerSelectName}_drawer_btn_ok`, doc),
      buttonTheme: 'primary',
      displayType: 'button',
      preloaderSubject: new BehaviorSubject(false),
      disabledSubject: new BehaviorSubject(false),
    },
    {
      id: Symbol('buttonId'),
      $name: 'DrawerButtonCancel',
      $type: FormButtonType.Cancel,
      label: DocHelper.getMessage('msg_cancel', doc),
      buttonTheme: 'primary',
      displayType: 'link',
      preloaderSubject: new BehaviorSubject(false),
      disabledSubject: new BehaviorSubject(false),
    },
  ];
}
